import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import { Card, Stack, Container, Typography, TablePagination, IconButton, Button, Tooltip, CircularProgress } from '@mui/material';
import Page from '../../components/Page';
import { ListToolbar } from '../../components/tables';
import useQuery from '../../hooks/useQuery';
import AgGridWrapper, { agDefaultColDef } from '../../components/tables/AgGridWrapper';
import { fDateOnly } from '../../utils/formatTime';
import tracker from '../../api/tracker';
import FuneralPrintTemplate from '../../sections/@dashboard/funerals/FuneralPrintTemplate';

const override = { margin: '0 auto' };

export default function FuneralIndex() {
  const [funerals, setFunerals] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, per_page: 15, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sort] = useState('desc');
  const [sortField] = useState('created_at');
  const [isLoading, setIsLoading] = useState(true);
  const [printingHashid, setPrintingHashid] = useState(null);
  const [printFuneral, setPrintFuneral] = useState(null);

  useEffect(() => {
    if (!printFuneral) return;
    const afterPrint = () => setPrintFuneral(null);
    window.addEventListener('afterprint', afterPrint);
    window.print();
    return () => window.removeEventListener('afterprint', afterPrint);
  }, [printFuneral]);

  const fetchData = useQuery();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const refresh = async () => {
    setIsLoading(true);
    await fetchData('funerals', filter, sort, setFunerals, sortField, setPagination, page);
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refresh, 500);
    return () => clearTimeout(delay);
  }, [filter, page]);

  const handlePrint = async (e, hashid) => {
    e.stopPropagation();
    setPrintingHashid(hashid);
    try {
      const response = await tracker.get(`funerals/${hashid}`);
      setPrintFuneral(response.data.data);
    } finally {
      setPrintingHashid(null);
    }
  };

  const columnDefs = [
    { field: 'deceased_name',     headerName: t('deceased_name'),     flex: 1, minWidth: 160, filter: 'agTextColumnFilter' },
    { field: 'date_of_death',     headerName: t('date_of_death'),     flex: 1, minWidth: 130, valueFormatter: ({ value }) => fDateOnly(value, i18n.language) },
    { field: 'next_of_kin',       headerName: t('next_of_kin'),       flex: 1, minWidth: 150, filter: 'agTextColumnFilter' },
    { field: 'funeral_home_name', headerName: t('funeral_home_name'), flex: 1, minWidth: 150, filter: 'agTextColumnFilter' },
    { field: 'mass_date',         headerName: t('mass_date'),         flex: 1, minWidth: 130, valueFormatter: ({ value }) => fDateOnly(value, i18n.language) },
    {
      headerName: '',
      width: 140,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => {
        const hid = params.data.hashid;
        const isPrinting = printingHashid === hid;
        return (
          <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Tooltip title={t('edit')}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/funeral/edit/${hid}`); }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="View">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/funeral/view/${hid}`); }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('print')}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={isPrinting}
                  onClick={(e) => handlePrint(e, hid)}
                >
                  {isPrinting
                    ? <CircularProgress size={14} />
                    : <PrintIcon fontSize="small" />
                  }
                </IconButton>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <>
    <FuneralPrintTemplate funeral={printFuneral} />
    <Page title="Funerals" className="no-print">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>{t('funerals')}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/dashboard/funeral/create')}>
            {t('new_funeral')}
          </Button>
        </Stack>
        <Card>
          <ListToolbar
            numSelected={0}
            filterName={filter}
            onFilterName={(e) => setFilter(e.target.value)}
            placeholder={t('search_funerals...')}
          />
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper
              rowData={funerals}
              columnDefs={columnDefs}
              defaultColDef={agDefaultColDef}
              clickable
              onRowClicked={(params) => {
                if (params.event?.target?.closest('button')) return;
                navigate(`/dashboard/funeral/view/${params.data.hashid}`);
              }}
            />
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 15}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
          />
        </Card>
      </Container>
    </Page>
    </>
  );
}
